import { useEffect, useState } from "react";
import { marked } from "marked";
import { apiCall } from "../lib/api";

type RecentQuery = { prompt: string; dateStr: string };
type AIResponse = {
  response: string;
  sql?: string;
  resultCount?: number;
  debugInfo?: { originalError?: string; generatedSQL?: string };
};

export function AISearchModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [input, setInput] = useState("");
  const [recent, setRecent] = useState<RecentQuery[]>([]);
  const [showRecent, setShowRecent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AIResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setInput("");
    setResult(null);
    setError(null);
    document.body.style.overflow = "hidden";
    apiCall<{ queries?: RecentQuery[] }>("ai-recent")
      .then((d) => {
        setRecent(d.queries || []);
        setShowRecent(!!d.queries?.length);
      })
      .catch(() => setShowRecent(false));
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  async function run(prompt: string) {
    const p = prompt.trim();
    if (!p) return;
    setShowRecent(false);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await apiCall<AIResponse>("ai-query", { method: "POST", body: JSON.stringify({ prompt: p }) });
      setResult(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      style={{ display: "flex" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ai-modal-container">
        <input
          type="text"
          id="aiSearchInput"
          placeholder="Ask me anything about your data..."
          autoComplete="off"
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") run(input);
          }}
        />
        <div className="ai-recent-searches" style={{ display: showRecent && recent.length ? "block" : "none" }}>
          <div className="ai-recent-header">Recent Searches</div>
          {recent.map((q, i) => (
            <div
              key={i}
              className="ai-recent-item"
              onClick={() => {
                setInput(q.prompt);
                run(q.prompt);
              }}
            >
              <span className="ai-recent-prompt">{q.prompt}</span>
              <span className="ai-recent-date">{q.dateStr}</span>
            </div>
          ))}
        </div>
        <div className={`ai-results${loading ? " loading" : ""}`} style={{ display: loading || result || error ? "block" : "none" }}>
          {loading && <div className="loading">Thinking...</div>}
          {!loading && error && (
            <div className="ai-response">
              <p style={{ color: "#e74c3c" }}>
                Sorry, I encountered an error while processing your question. Please try rephrasing it or ask something else.
              </p>
              <p style={{ color: "#999", fontSize: 14 }}>Error: {error}</p>
            </div>
          )}
          {!loading && result && (
            <>
              <div className="ai-response" dangerouslySetInnerHTML={{ __html: marked.parse(result.response) as string }} />
              {result.debugInfo && (
                <div className="ai-sql-debug" style={{ backgroundColor: "#ffe6e6", border: "1px solid #ff9999" }}>
                  <strong>Debug Info:</strong>
                  <br />
                  <strong>Error:</strong> {result.debugInfo.originalError}
                  <br />
                  <strong>Generated SQL:</strong>
                  <br />
                  <code>{result.debugInfo.generatedSQL}</code>
                </div>
              )}
              {result.sql && (
                <div className="ai-sql-debug">
                  <strong>Generated SQL:</strong>
                  <br />
                  <code>{result.sql}</code>
                  <br />
                  <strong>Results:</strong> {result.resultCount} rows
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
